import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "marchuk_chat_session";

export default function AIChatWidget() {
  const { t, lang } = useT();

  const makeIntro = () => ({ role: "assistant", content: t("chat.intro") });

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([makeIntro()]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(STORAGE_KEY);
  });
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // When language changes and the conversation only contains the intro, refresh it.
  useEffect(() => {
    setMessages((prev) => (prev.length === 1 ? [makeIntro()] : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 350);
      setUnread(false);
    }
  }, [open]);

  useEffect(() => {
    const tt = setTimeout(() => {
      if (!open && messages.length === 1) setUnread(true);
    }, 8000);
    return () => clearTimeout(tt);
  }, [open, messages.length]);

  const sendMessage = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(
        `${API}/ai/chat`,
        { session_id: sessionId || undefined, message: trimmed, lang },
        { timeout: 45000 }
      );
      const { session_id, response } = res.data;
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        try {
          window.localStorage.setItem(STORAGE_KEY, session_id);
        } catch (e) {
          // ignore
        }
      }
      setMessages((prev) => [...prev, { role: "assistant", content: response }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: t("chat.error"), error: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const resetChat = () => {
    setMessages([makeIntro()]);
    setSessionId(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  const suggestions = t("chat.suggestions");

  return (
    <>
      <button
        data-testid="ai-chat-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t("chat.closeAria") : t("chat.openAria")}
        className={`fixed bottom-24 md:bottom-6 right-6 z-[70] h-14 w-14 md:h-16 md:w-16 rounded-full shadow-[0_20px_60px_-15px_rgba(164,155,255,0.6)] transition-all duration-500 grid place-items-center overflow-hidden group ${
          open ? "scale-90 rotate-90" : "hover:scale-105"
        }`}
      >
        <span className="absolute inset-0 iridescent" />
        <span className="absolute inset-[2px] rounded-full bg-[#0F0524] grid place-items-center">
          {open ? (
            <X className="h-5 w-5 text-white" />
          ) : (
            <MessageCircle className="h-5 w-5 text-white" />
          )}
        </span>
        {!open && unread && (
          <>
            <span className="absolute top-1.5 right-1.5 h-3 w-3 rounded-full bg-[#FF00E5] animate-ping" />
            <span className="absolute top-1.5 right-1.5 h-3 w-3 rounded-full bg-[#FF00E5]" />
          </>
        )}
      </button>

      <div
        data-testid="ai-chat-panel"
        className={`fixed bottom-44 md:bottom-24 right-4 md:right-6 z-[65] w-[calc(100vw-2rem)] sm:w-[400px] max-h-[78vh] rounded-3xl overflow-hidden transition-all duration-500 origin-bottom-right ${
          open
            ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
        }`}
        style={{ boxShadow: "0 30px 80px -20px rgba(15,5,36,0.3)" }}
      >
        <div className="glass rounded-3xl overflow-hidden flex flex-col h-[78vh] max-h-[640px]">
          <div className="relative px-5 py-4 text-white" style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}>
            <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at top right, rgba(255,0,229,0.4), transparent 60%)" }} />
            <div className="relative flex items-center gap-3">
              <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl overflow-hidden">
                <span className="absolute inset-0 iridescent" />
                <span className="absolute inset-[2px] rounded-[10px] bg-[#0F0524] grid place-items-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </span>
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base font-semibold leading-tight">{t("chat.title")}</div>
                <div className="text-[11px] text-white/60 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00FFA3] animate-pulse" />
                  {t("chat.online")}
                </div>
              </div>
              <button
                data-testid="ai-chat-reset"
                onClick={resetChat}
                className="text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white/90 transition"
                title={t("chat.reset")}
              >
                {t("chat.reset")}
              </button>
            </div>
          </div>

          <div ref={scrollRef} data-testid="ai-chat-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-white/40 no-scroll-x">
            {messages.map((m, i) => (
              <MessageBubble key={i} msg={m} />
            ))}
            {loading && (
              <div data-testid="ai-chat-loading" className="flex items-center gap-2 text-[13px] text-[#5C527A] pl-2">
                <span className="h-2 w-2 rounded-full bg-[#A49BFF] animate-bounce" />
                <span className="h-2 w-2 rounded-full bg-[#FF00E5] animate-bounce" style={{ animationDelay: "0.15s" }} />
                <span className="h-2 w-2 rounded-full bg-[#00F0FF] animate-bounce" style={{ animationDelay: "0.3s" }} />
                <span className="ml-1">{t("chat.thinking")}</span>
              </div>
            )}
          </div>

          {messages.length === 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  data-testid={`ai-suggestion-${idx}`}
                  onClick={() => sendMessage(s)}
                  className="text-[11.5px] font-medium rounded-full glass border border-[#E5E3F0] px-3 py-1.5 text-[#0F0524] hover:bg-white transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="px-3 pb-3 pt-2 bg-white/60 border-t border-white/60">
            <div className="relative flex items-end gap-2 rounded-2xl bg-white border border-[#E5E3F0] focus-within:border-transparent focus-within:shadow-[0_0_0_1.5px_#A49BFF] transition px-3 py-2">
              <textarea
                ref={inputRef}
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder={t("chat.placeholder")}
                className="flex-1 resize-none bg-transparent outline-none text-[14px] text-[#0F0524] placeholder:text-[#5C527A]/50 py-1.5 max-h-28"
              />
              <button
                data-testid="ai-chat-send"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="h-9 w-9 shrink-0 rounded-xl grid place-items-center text-white disabled:opacity-40 transition-all"
                style={{ background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" }}
                aria-label="Send"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-2 text-[10px] text-[#5C527A]/60 text-center">
              {t("chat.footer")} · <span className="grad-text font-semibold">MARCHUK LLC</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div
      data-testid={isUser ? "ai-msg-user" : "ai-msg-assistant"}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "text-white"
            : msg.error
              ? "bg-[#FFE6F0] text-[#B00050] border border-[#F9B8D2]"
              : "bg-white border border-[#E5E3F0] text-[#0F0524]"
        }`}
        style={isUser ? { background: "linear-gradient(135deg, #0F0524 0%, #2A1458 100%)" } : {}}
      >
        {renderContent(msg.content)}
      </div>
    </div>
  );
}

function renderContent(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}
